import { ReactElement } from 'react';
import { UIIcon, UIText, UITooltip } from '@/ui';
import { charcoal } from '@/ui/styles';
import styles from './pending-badge.module.scss';

export type PendingBadgeProps = {
    /** Text shown inside the pill, e.g. "Pending". */
    label: string;
    /**
     * Hover/accessible text. When the pill is clickable this should describe the
     * action (e.g. "Cancel change"); otherwise it explains the pending state.
     */
    tooltip?: string;
    /** When set, the whole pill is clickable and cancels the pending change. */
    onCancel?: () => void;
};

/**
 * Amber "Pending" pill with a gently pulsing dot — the shared indicator for a
 * device command that has been submitted but not yet downloaded by the device.
 * Uses the app's established pending amber (`$yellow`). When `onCancel` is set the
 * entire pill becomes a clickable cancel control (hover-highlighted, tooltipped),
 * with an inline "×" affordance.
 */
export function PendingBadge({
    label,
    tooltip,
    onCancel
}: PendingBadgeProps): ReactElement {
    const content = (
        <>
            <span className={styles.dot} />
            <UIText
                variant="small"
                medium
                type="default"
                style={{ lineHeight: 1 }}
            >
                {label}
            </UIText>
            {onCancel && (
                <UIIcon
                    name="x"
                    size={14}
                    color={charcoal}
                    className={styles.x}
                />
            )}
        </>
    );

    const pill = onCancel ? (
        <button
            type="button"
            className={`${styles.badge} ${styles.clickable}`}
            onClick={onCancel}
            aria-label={tooltip ?? label}
        >
            {content}
        </button>
    ) : (
        <div className={styles.badge}>{content}</div>
    );

    return tooltip ? <UITooltip text={tooltip}>{pill}</UITooltip> : pill;
}
